import React, { useState } from 'react';
import Cookies from 'js-cookie';
import {
  Dialog,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Button,
  RadioGroup,
  Radio,
} from '@mui/material';
import { ILoader } from '~/_base/components/helixIcons';
import makeStyles from '@mui/styles/makeStyles';
import { useToast } from '~/_base/context/toastContext';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

type Props = {
  onSave: () => void;
  onClose: () => void;
  open: boolean;
  id: number | null;
};

export default function LocalesDialog(props: Props) {
  const classes = useStyles();
  const { warningToast, successToast } = useToast();
  const [saving, setSaving] = useState(false);
  const localeFromCookie = Cookies.get('locale');
  const localeFromBrowser = navigator.language.split('-')[0];
  const currentLocale = localeFromCookie || localeFromBrowser;
  const [selectedLocale, setSelectedLocale] = useState(currentLocale);
  const { i18n, t } = useTranslation();
  const languageValues = [
    { value: 'en', label: t('English') },
    { value: 'en-US', label: t('English (US)') },
    { value: 'es', label: t('Spanish') },
    { value: 'fr', label: t('French') },
  ];

  function saveChange() {
    setSaving(true);
    if (selectedLocale) {
      Cookies.set('locale', selectedLocale);
      i18n.changeLanguage(selectedLocale);
      moment.locale(selectedLocale);
      successToast(t('Language updated!'));
      setSaving(false);
      props.onSave();
    } else {
      setSaving(false);
      warningToast(t('Please select a language to proceed.'));
    }
  }

  return (
    <Dialog
      data-testid="locales_dialog"
      open={props.open}
      onClose={e => {
        props.onClose();
      }}
      PaperProps={{
        style: {
          width: '90vw',
          maxWidth: '600px',
          height: '45vh',
          maxHeight: '400px',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h3" className={classes.title}>
          {t('Change language')}
        </Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.tabContent }}>
        <RadioGroup
          style={{ marginBottom: 8 }}
          name="locales_available"
          value={selectedLocale}
          onChange={(e, val) => {
            setSelectedLocale(val);
          }}
        >
          {languageValues.map(lang => (
            <FormControlLabel
              key={lang.value}
              value={lang.value}
              control={<Radio />}
              label={lang.label}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          variant="contained"
          color="secondary"
          onClick={e => {
            props.onClose();
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          sx={{ minWidth: '100px' }}
          startIcon={
            saving && (
              <ILoader fontSize="small" />
            )
          }
          variant="contained"
          color="primary"
          onClick={e => saveChange()}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  dialogActions: {
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  title: {
    paddingLeft: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  tabContent: {
    borderTop: '1px solid #e6eaec',
    marginTop: '-1px',
  },
}));
